import { useCallback, useEffect, useState } from "react";

const fullscreenElement = () =>
  typeof document === "undefined" ? null : document.fullscreenElement || document.webkitFullscreenElement || null;

const nativeSupported = () =>
  typeof document !== "undefined" && !!(document.fullscreenEnabled || document.webkitFullscreenEnabled);

const lockLandscape = () => {
  try {
    const p = window.screen?.orientation?.lock?.("landscape");
    if (p && p.catch) p.catch(() => {});
  } catch {
    /* not every browser lets a page turn the screen */
  }
};

const unlockOrientation = () => {
  try {
    window.screen?.orientation?.unlock?.();
  } catch {
    /* nothing was locked */
  }
};

/**
 * useElementFullscreen — full screen for ONE element, the player's own box.
 *
 * Never the <video>: a video element in the browser's full screen is drawn
 * alone, so the watermark and every overlay over it disappear. The box takes
 * them with it.
 *
 * iPhone Safari cannot put anything but a bare video in full screen (no
 * Fullscreen API for elements), so there the box is PINNED over the viewport
 * instead — `isPseudo` — with the page behind it held still and Escape to
 * leave. Rotating the phone then gives a full-screen picture with the
 * watermark still on it. The same fallback catches a browser that refuses the
 * request.
 *
 * @param {React.RefObject<HTMLElement>} ref
 * @param {object}   [opts]
 * @param {Function} [opts.wantsLandscape]  asked on entering; true turns a phone sideways (where the browser allows it)
 */
export default function useElementFullscreen(ref, { wantsLandscape } = {}) {
  const [native, setNative] = useState(false);
  const [pseudo, setPseudo] = useState(false);

  useEffect(() => {
    const onChange = () => {
      const on = !!ref.current && fullscreenElement() === ref.current;
      setNative(on);
      if (!on) unlockOrientation();
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [ref]);

  useEffect(() => {
    if (!pseudo) return undefined;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setPseudo(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      root.style.overflow = before;
      document.removeEventListener("keydown", onKey);
    };
  }, [pseudo]);

  const enter = useCallback(async () => {
    const el = ref.current;
    if (!el) return;
    if (nativeSupported()) {
      try {
        if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: "hide" });
        else el.webkitRequestFullscreen();
        if (wantsLandscape?.()) lockLandscape();
        return;
      } catch {
        /* refused (no user gesture, an iframe without allow) — pin it instead */
      }
    }
    setPseudo(true);
  }, [ref, wantsLandscape]);

  const exit = useCallback(() => {
    if (pseudo) setPseudo(false);
    if (fullscreenElement() && ref.current && fullscreenElement() === ref.current) {
      try {
        const p = document.exitFullscreen ? document.exitFullscreen() : document.webkitExitFullscreen?.();
        if (p && p.catch) p.catch(() => {});
      } catch {
        /* already out */
      }
    }
  }, [pseudo, ref]);

  const isFullscreen = native || pseudo;
  const toggle = useCallback(() => (isFullscreen ? exit() : enter()), [isFullscreen, enter, exit]);

  return { isFullscreen, isPseudo: pseudo, enter, exit, toggle };
}
